import { useEffect, useRef, useState, ReactNode } from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useTheme,
  Tooltip,
} from "@mui/material";
import { CheckCircleIconCustomSize, SignatureIcon } from "components";

export type IconOptionsMenuOption = {
  label: string;
  value: string;
  icon?: ReactNode;
  disabled?: boolean;
  hidden?: boolean;
};

export function IconOptionsMenu({
  tooltipTitle = "Options",
  buttonIcon,
  buttonIconSx,
  options,
  selected = [],
  onChange,
  selectionMode = "single",
  menuPaperSx,
  loadingValue = null,
  keepOpenOnSelect = false,
  disabled = false,
}: {
  tooltipTitle?: string;
  buttonIcon?: ReactNode;
  buttonIconSx?: any;
  options: IconOptionsMenuOption[];
  selected?: string[];
  onChange: (nextSelected: string[]) => void;
  selectionMode?: "single" | "multiple";
  menuPaperSx?: any;
  /**
   * When set, shows a circular loader on the option whose value matches and
   * disables every other option (prevents double-click while async work runs).
   * When it transitions from set → null the menu auto-closes.
   */
  loadingValue?: string | null;
  /**
   * If true, clicking an option does NOT close the menu. Caller is expected
   * to drive closure via `loadingValue` (menu closes when loadingValue goes
   * from set → null).
   */
  keepOpenOnSelect?: boolean;
  /**
   * If true, the trigger button does nothing on click — the menu never opens.
   * Use this when the whole action is unavailable (vs. disabling individual
   * options, which still lets the menu open).
   */
  disabled?: boolean;
}) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const prevLoadingValue = useRef<string | null>(loadingValue);

  // Auto-close when loadingValue transitions from set → null (async op done).
  useEffect(() => {
    if (prevLoadingValue.current && !loadingValue) {
      setAnchorEl(null);
    }
    prevLoadingValue.current = loadingValue;
  }, [loadingValue]);

  const handleToggle = (value: string) => {
    let next: string[] = [];
    const isSelected = selected.includes(value);

    if (selectionMode === "single") {
      next = isSelected ? [] : [value];
    } else {
      next = isSelected
        ? selected.filter((v) => v !== value)
        : [...selected, value];
    }
    onChange(next);
    if (!keepOpenOnSelect) {
      setAnchorEl(null);
    }
  };

  return (
    <>
      <Tooltip title={tooltipTitle}>
        <IconButton
          disableRipple={disabled}
          onClick={(e) => {
            if (disabled) return;
            setAnchorEl(e.currentTarget);
          }}
          sx={
            buttonIconSx
              ? buttonIconSx
              : {
                  width: "20px",
                  height: "20px",
                  ":hover": { bgcolor: "transparent" },
                }
          }
        >
          {buttonIcon || <SignatureIcon />}
        </IconButton>
      </Tooltip>
      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        sx={{ "& .MuiList-root": { p: "0px", pt: "8px", pb: "8px" } }}
        PaperProps={{
          sx: {
            margin: "16px 0px",
            padding: "0px",
            overflow: "scroll",
            maxHeight: "200px",
            width: "210px",
            maxWidth: "230px",
            backgroundColor: "#FFFFFF",
            overflowX: "clip",
            minWidth: "175px",
            borderRadius: "8px",
            boxShadow:
              "0px 0px 4px 0px rgba(0, 0, 0, 0.06), 0px 0px 2px 0px rgba(0, 0, 0, 0.04)",
            border: `1px solid ${theme.palette.neutral["015"]}`,
            ...menuPaperSx,
          },
        }}
      >
        {options
          .filter((opt) => !opt.hidden)
          .map((opt) => {
            const isSelected = selected.includes(opt.value);
            // Disable every option while ANY option is loading so users can't
            // fire a second op mid-flight.
            const isDisabled = !!opt.disabled || !!loadingValue;
            return (
              <MenuItem
                key={opt.value}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isDisabled) handleToggle(opt.value);
                }}
                disabled={isDisabled}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 1,
                  backgroundColor: isSelected
                    ? theme.palette.neutral["B-005"]
                    : "transparent",
                  ":hover": {
                    bgcolor: isSelected ? theme.palette.neutral["B-005"] : "",
                  },
                }}
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  {opt.icon}
                  <Typography
                    variant="body-small"
                    sx={{ fontWeight: isSelected ? "bold" : "normal" }}
                  >
                    {opt.label}
                  </Typography>
                </Stack>
                {isSelected && (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <CheckCircleIconCustomSize height="20px" color="success" />
                  </Box>
                )}
              </MenuItem>
            );
          })}
      </Menu>
    </>
  );
}
